/**
 * Room Preview - Shopify asset
 * Upload room photo, place product on it with full transform control:
 * drag, scale, rotate (360°), flip, opacity, and an automatic white-background
 * removal pass so product photos blend naturally over the room.
 */
(function () {
  "use strict";

  const section = document.getElementById("room-preview-section");
  if (!section) return;

  /* DOM */
  const uploadArea  = document.getElementById("rp-upload-area");
  const fileInput   = document.getElementById("rp-file-input");
  const uploadBtn   = document.getElementById("rp-upload-btn");
  const sampleBtn   = document.getElementById("rp-sample-btn");
  const editor      = document.getElementById("rp-editor");
  const canvas      = document.getElementById("rp-canvas");
  const ctx         = canvas.getContext("2d");
  const loadingEl   = document.getElementById("rp-loading");

  const scaleSlider   = document.getElementById("rp-scale");
  const opacitySlider = document.getElementById("rp-opacity");
  const rotateSlider  = document.getElementById("rp-rotate");
  const bgRemoveSlider= document.getElementById("rp-bgremove");
  const flipBtn       = document.getElementById("rp-flip-btn");
  const centerBtn     = document.getElementById("rp-center-btn");
  const resetBtn      = document.getElementById("rp-reset-btn");
  const downloadBtn   = document.getElementById("rp-download-btn");

  /* Config */
  const CANVAS_MAX_W = 1400;
  const SAMPLE_ROOM  = section.dataset.sampleRoom;
  const productSrc   = section.dataset.productImage;
  const productTitle = section.dataset.productTitle || "product";

  /* State */
  let roomImg    = null;
  let productOriginalImg = null;
  let productImg = null;       // canvas with possibly bg-removed
  let productLoaded = false;
  let bgThreshold = bgRemoveSlider ? parseFloat(bgRemoveSlider.value) : 70;

  let state = {
    x: 0, y: 0,
    scale: 0.30,
    opacity: 1,
    rotation: 0,                // radians, 0..2π
    flipped: false
  };
  let drag    = { active: false, mode: null, startX: 0, startY: 0, origX: 0, origY: 0, origRot: 0, origScale: 0, centerX: 0, centerY: 0 };
  let pinch   = { active: false, startDist: 0, startScale: 0, startAngle: 0, startRot: 0 };

  /* Load product image */
  if (productSrc) {
    productOriginalImg = new Image();
    productOriginalImg.crossOrigin = "anonymous";
    productOriginalImg.onload = () => {
      productImg = makeProductCanvas(productOriginalImg, bgThreshold);
      productLoaded = true;
      if (roomImg) render();
    };
    productOriginalImg.onerror = () => { productOriginalImg = null; productLoaded = false; };
    productOriginalImg.src = productSrc;
  }

  /* Edge-aware background removal:
     Flood-fills from the 4 corners of the image, only erasing connected
     light/low-saturation pixels. Product's interior whites are preserved.
     threshold 0 = off, 100 = aggressive. */
  function makeProductCanvas(img, threshold) {
    const c = document.createElement("canvas");
    c.width  = img.naturalWidth;
    c.height = img.naturalHeight;
    const cctx = c.getContext("2d");
    cctx.drawImage(img, 0, 0);

    if (threshold <= 0) return c;

    let imgData;
    try {
      imgData = cctx.getImageData(0, 0, c.width, c.height);
    } catch (e) {
      return c; // CORS — return as-is
    }

    const W = c.width, H = c.height;
    const data = imgData.data;

    // tolerance: how different a pixel can be from the "seed" corner color
    // threshold 0..100 → tol 8..70
    const tol = 8 + (threshold * 0.62);

    // Sample seed: average of the 4 corner regions (8x8 each)
    const seed = sampleCornerColor(data, W, H);

    // If the corners aren't even close to white-ish, do nothing (e.g. dark backdrop)
    // We let it run anyway — but require corner brightness > 180 to bother
    const seedLight = (seed.r + seed.g + seed.b) / 3;
    if (seedLight < 175) return c;

    // BFS flood-fill from every edge pixel that matches seed color
    const visited = new Uint8Array(W * H);
    const queue = new Int32Array(W * H);
    let qHead = 0, qTail = 0;

    function tryEnqueue(x, y) {
      if (x < 0 || y < 0 || x >= W || y >= H) return;
      const idx = y * W + x;
      if (visited[idx]) return;
      const di = idx * 4;
      const r = data[di], g = data[di+1], b = data[di+2];
      // distance from seed color
      const dr = r - seed.r, dg = g - seed.g, db = b - seed.b;
      const dist = Math.sqrt(dr*dr + dg*dg + db*db);
      if (dist > tol) return;
      visited[idx] = 1;
      queue[qTail++] = idx;
    }

    // seed entire border
    for (let x = 0; x < W; x++) { tryEnqueue(x, 0); tryEnqueue(x, H-1); }
    for (let y = 0; y < H; y++) { tryEnqueue(0, y); tryEnqueue(W-1, y); }

    // BFS
    while (qHead < qTail) {
      const idx = queue[qHead++];
      const x = idx % W;
      const y = (idx / W) | 0;
      tryEnqueue(x+1, y);
      tryEnqueue(x-1, y);
      tryEnqueue(x, y+1);
      tryEnqueue(x, y-1);
    }

    // Apply transparency to visited pixels with feathering at the boundary
    for (let i = 0; i < visited.length; i++) {
      if (!visited[i]) continue;
      const di = i * 4;
      // soft edge: if any neighbor is unvisited, partially fade
      const x = i % W;
      const y = (i / W) | 0;
      let edge = false;
      if (x > 0   && !visited[i-1])  edge = true;
      if (x < W-1 && !visited[i+1])  edge = true;
      if (y > 0   && !visited[i-W])  edge = true;
      if (y < H-1 && !visited[i+W])  edge = true;
      if (edge) {
        data[di+3] = Math.round(data[di+3] * 0.4);
      } else {
        data[di+3] = 0;
      }
    }

    cctx.putImageData(imgData, 0, 0);
    return c;
  }

  function sampleCornerColor(data, W, H) {
    const samples = [];
    const PAD = 8;
    [
      [0, 0],
      [W - PAD, 0],
      [0, H - PAD],
      [W - PAD, H - PAD]
    ].forEach(([sx, sy]) => {
      for (let y = sy; y < sy + PAD; y++) {
        for (let x = sx; x < sx + PAD; x++) {
          const di = (y * W + x) * 4;
          samples.push([data[di], data[di+1], data[di+2]]);
        }
      }
    });
    let r = 0, g = 0, b = 0;
    samples.forEach(s => { r += s[0]; g += s[1]; b += s[2]; });
    return {
      r: Math.round(r / samples.length),
      g: Math.round(g / samples.length),
      b: Math.round(b / samples.length)
    };
  }

  function rebuildProduct() {
    if (!productOriginalImg) return;
    productImg = makeProductCanvas(productOriginalImg, bgThreshold);
    render();
  }

  /* Upload */
  function openFilePicker() { fileInput.click(); }
  uploadBtn.addEventListener("click", openFilePicker);
  uploadArea.addEventListener("click", (e) => {
    if (e.target.id === "rp-sample-btn") return;
    if (e.target === uploadArea || e.target.closest(".room-preview__upload-inner")) {
      openFilePicker();
    }
  });
  fileInput.addEventListener("change", (e) => {
    const file = e.target.files[0];
    if (file) loadRoomFile(file);
  });

  uploadArea.addEventListener("dragover", (e) => {
    e.preventDefault();
    uploadArea.classList.add("drag-over");
  });
  uploadArea.addEventListener("dragleave", () => uploadArea.classList.remove("drag-over"));
  uploadArea.addEventListener("drop", (e) => {
    e.preventDefault();
    uploadArea.classList.remove("drag-over");
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) loadRoomFile(file);
  });

  if (sampleBtn && SAMPLE_ROOM) {
    sampleBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      loadRoomFromUrl(SAMPLE_ROOM);
    });
  }

  function loadRoomFile(file) {
    if (file.size > 10 * 1024 * 1024) {
      alert("Please choose an image under 10 MB.");
      return;
    }
    if (!file.type.startsWith("image/")) {
      alert("Please choose an image file.");
      return;
    }
    showLoading(true);
    const reader = new FileReader();
    reader.onload  = (e) => loadRoomFromDataUrl(e.target.result);
    reader.onerror = () => { showLoading(false); alert("Could not read this file."); };
    reader.readAsDataURL(file);
  }

  function loadRoomFromUrl(url) {
    showLoading(true);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload  = () => onRoomReady(img);
    img.onerror = () => { showLoading(false); alert("Could not load sample image."); };
    img.src = url;
  }

  function loadRoomFromDataUrl(dataUrl) {
    const img = new Image();
    img.onload  = () => onRoomReady(img);
    img.onerror = () => { showLoading(false); alert("Image failed to load."); };
    img.src = dataUrl;
  }

  function onRoomReady(img) {
    roomImg = img;
    initCanvas();
    centerProduct();
    showLoading(false);
    uploadArea.style.display = "none";
    editor.style.display = "block";
    render();
  }

  function showLoading(show) {
    if (loadingEl) loadingEl.style.display = show ? "flex" : "none";
  }

  /* Canvas */
  function initCanvas() {
    const w = Math.min(roomImg.naturalWidth, CANVAS_MAX_W);
    const h = Math.round(roomImg.naturalHeight * (w / roomImg.naturalWidth));
    canvas.width  = w;
    canvas.height = h;
  }

  function centerProduct() {
    state.scale    = parseFloat(scaleSlider.value) / 100;
    state.opacity  = parseFloat(opacitySlider.value) / 100;
    state.rotation = 0;
    state.flipped  = false;
    rotateSlider.value = 0;
    const w = pw(), h = ph();
    state.x = (canvas.width  - w) / 2;
    state.y = (canvas.height - h) / 2;
  }

  function pw() {
    if (!productImg) return 0;
    const ratio = productImg.width / productImg.height;
    return canvas.width * state.scale;
  }
  function ph() {
    if (!productImg) return 0;
    return pw() * (productImg.height / productImg.width);
  }

  /* Render */
  function render() {
    if (!roomImg) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(roomImg, 0, 0, canvas.width, canvas.height);
    if (!productLoaded || !productImg) return;

    const w = pw(), h = ph();
    const cx = state.x + w / 2, cy = state.y + h / 2;

    ctx.save();
    ctx.globalAlpha = state.opacity;
    ctx.translate(cx, cy);
    ctx.rotate(state.rotation);
    if (state.flipped) ctx.scale(-1, 1);
    // soft realistic shadow only if any bg removed (product likely cutout)
    if (bgThreshold > 0) {
      ctx.shadowColor = "rgba(0,0,0,0.35)";
      ctx.shadowBlur = 28;
      ctx.shadowOffsetY = 14;
    }
    ctx.drawImage(productImg, -w / 2, -h / 2, w, h);
    ctx.restore();

    drawSelectionUI(cx, cy, w, h);
  }

  function rotHandleOffset(h) {
    // Place rotation handle inside the bounding box near the top
    // so it stays visible even if product is at canvas edge.
    return -h / 2 + 28;
  }

  function drawSelectionUI(cx, cy, w, h) {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(state.rotation);

    // dashed border
    ctx.strokeStyle = "rgba(255,255,255,0.95)";
    ctx.lineWidth   = 2;
    ctx.setLineDash([6, 4]);
    ctx.strokeRect(-w / 2, -h / 2, w, h);

    ctx.setLineDash([]);

    // resize handle (bottom-right) — bigger
    drawHandle(w / 2, h / 2, "#323C52", "#fff", false);

    // rotation handle (top-center, INSIDE bounds)
    const rotY = rotHandleOffset(h);
    drawHandle(0, rotY, "#fff", "#323C52", true);

    ctx.restore();
  }

  function drawHandle(x, y, fill, stroke, isRot) {
    const r = isRot ? 16 : 14;
    // soft white halo so handle pops on any background
    ctx.beginPath();
    ctx.arc(x, y, r + 3, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255,255,255,0.8)";
    ctx.fill();

    ctx.fillStyle = fill;
    ctx.strokeStyle = stroke || "#323C52";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    if (isRot) {
      // rotation glyph (curved arrow)
      ctx.strokeStyle = "#323C52";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(x, y, 7, -Math.PI * 0.75, Math.PI * 0.6);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x + 5.5, y + 2);
      ctx.lineTo(x + 7, y + 7);
      ctx.lineTo(x + 1.8, y + 7);
      ctx.closePath();
      ctx.fillStyle = "#323C52";
      ctx.fill();
    } else {
      // resize glyph (diagonal arrows)
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x - 4, y - 4);
      ctx.lineTo(x + 4, y + 4);
      ctx.moveTo(x - 4, y + 4);
      ctx.lineTo(x + 4, y - 4);
      ctx.stroke();
    }
  }

  /* Sliders */
  scaleSlider.addEventListener("input", () => updateScale(parseFloat(scaleSlider.value) / 100));
  opacitySlider.addEventListener("input", () => {
    state.opacity = parseFloat(opacitySlider.value) / 100;
    render();
  });
  rotateSlider.addEventListener("input", () => {
    state.rotation = (parseFloat(rotateSlider.value) * Math.PI) / 180;
    render();
  });
  if (bgRemoveSlider) {
    bgRemoveSlider.addEventListener("input", () => {
      bgThreshold = parseFloat(bgRemoveSlider.value);
      rebuildProduct();
    });
  }
  if (flipBtn) flipBtn.addEventListener("click", () => {
    state.flipped = !state.flipped;
    render();
  });
  if (centerBtn) centerBtn.addEventListener("click", () => {
    centerProduct();
    render();
  });

  function updateScale(newScale) {
    const oldW = pw(), oldH = ph();
    state.scale = Math.max(0.05, Math.min(2.0, newScale));
    scaleSlider.value = Math.round(state.scale * 100);
    const newW = pw(), newH = ph();
    state.x += (oldW - newW) / 2;
    state.y += (oldH - newH) / 2;
    render();
  }

  /* Hit-test which handle/area was hit */
  function whichHit(x, y) {
    if (!productLoaded) return null;
    const w = pw(), h = ph();
    const cx = state.x + w / 2, cy = state.y + h / 2;
    const dx = x - cx, dy = y - cy;
    const cos = Math.cos(-state.rotation), sin = Math.sin(-state.rotation);
    const lx = cos * dx - sin * dy;
    const ly = sin * dx + cos * dy;

    // rotation handle (inside top area)
    const rotY = rotHandleOffset(h);
    if (Math.hypot(lx - 0, ly - rotY) <= 26) return "rotate";
    // resize handle (bottom-right) — bigger hit area
    if (Math.hypot(lx - w / 2, ly - h / 2) <= 26) return "resize";
    // body
    if (Math.abs(lx) <= w / 2 + 10 && Math.abs(ly) <= h / 2 + 10) return "move";
    return null;
  }

  /* Mouse */
  canvas.addEventListener("mousedown", (e) => onPointerDown(e.offsetX, e.offsetY));
  window.addEventListener("mousemove", (e) => {
    if (!drag.active) return;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (canvas.width / rect.width);
    const y = (e.clientY - rect.top)  * (canvas.height / rect.height);
    onPointerMove(x, y);
  });
  window.addEventListener("mouseup", onPointerUp);

  /* Mouse wheel — zoom */
  canvas.addEventListener("wheel", (e) => {
    if (!roomImg || !productLoaded) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.04 : 0.04;
    updateScale(state.scale + delta);
  }, { passive: false });

  /* Touch */
  canvas.addEventListener("touchstart", (e) => {
    if (!productLoaded) return;
    if (e.touches.length === 2) {
      e.preventDefault();
      const t1 = canvasTouchPos(e.touches[0]);
      const t2 = canvasTouchPos(e.touches[1]);
      pinch.active = true;
      pinch.startDist = Math.hypot(t1.x - t2.x, t1.y - t2.y);
      pinch.startScale = state.scale;
      pinch.startAngle = Math.atan2(t2.y - t1.y, t2.x - t1.x);
      pinch.startRot = state.rotation;
      drag.active = false;
    } else if (e.touches.length === 1) {
      e.preventDefault();
      const t = canvasTouchPos(e.touches[0]);
      onPointerDown(t.x, t.y);
    }
  }, { passive: false });

  canvas.addEventListener("touchmove", (e) => {
    if (e.touches.length === 2 && pinch.active) {
      e.preventDefault();
      const t1 = canvasTouchPos(e.touches[0]);
      const t2 = canvasTouchPos(e.touches[1]);
      const d = Math.hypot(t1.x - t2.x, t1.y - t2.y);
      const a = Math.atan2(t2.y - t1.y, t2.x - t1.x);
      updateScale(pinch.startScale * (d / pinch.startDist));
      state.rotation = pinch.startRot + (a - pinch.startAngle);
      rotateSlider.value = Math.round(((state.rotation * 180 / Math.PI) % 360 + 540) % 360 - 180);
      render();
    } else if (e.touches.length === 1 && drag.active) {
      e.preventDefault();
      const t = canvasTouchPos(e.touches[0]);
      onPointerMove(t.x, t.y);
    }
  }, { passive: false });

  canvas.addEventListener("touchend", (e) => {
    if (e.touches.length < 2) pinch.active = false;
    if (e.touches.length === 0) onPointerUp();
  });

  function canvasTouchPos(touch) {
    const rect = canvas.getBoundingClientRect();
    return {
      x: (touch.clientX - rect.left) * (canvas.width  / rect.width),
      y: (touch.clientY - rect.top)  * (canvas.height / rect.height)
    };
  }

  function onPointerDown(x, y) {
    const hit = whichHit(x, y);
    if (!hit) return;
    const w = pw(), h = ph();
    drag = {
      active: true,
      mode: hit,
      startX: x, startY: y,
      origX: state.x, origY: state.y,
      origRot: state.rotation,
      origScale: state.scale,
      centerX: state.x + w / 2,
      centerY: state.y + h / 2
    };
    canvas.classList.add("grabbing");
    if (hit === "rotate") canvas.style.cursor = "grabbing";
  }

  function onPointerMove(x, y) {
    if (!drag.active) return;
    if (drag.mode === "move") {
      state.x = drag.origX + (x - drag.startX);
      state.y = drag.origY + (y - drag.startY);
    } else if (drag.mode === "rotate") {
      // current angle of pointer from center
      const curAng   = Math.atan2(y - drag.centerY, x - drag.centerX);
      const startAng = Math.atan2(drag.startY - drag.centerY, drag.startX - drag.centerX);
      let ang = drag.origRot + (curAng - startAng);
      // wrap to [-π, π]
      while (ang > Math.PI)  ang -= 2 * Math.PI;
      while (ang < -Math.PI) ang += 2 * Math.PI;
      state.rotation = ang;
      rotateSlider.value = Math.round(ang * 180 / Math.PI);
    } else if (drag.mode === "resize") {
      // distance from center scales proportionally
      const startDist = Math.hypot(drag.startX - drag.centerX, drag.startY - drag.centerY);
      const curDist   = Math.hypot(x - drag.centerX, y - drag.centerY);
      if (startDist > 0) {
        const ratio = curDist / startDist;
        const oldW = pw(), oldH = ph();
        state.scale = Math.max(0.05, Math.min(2.0, drag.origScale * ratio));
        scaleSlider.value = Math.round(state.scale * 100);
        const newW = pw(), newH = ph();
        // keep center stable
        state.x = drag.centerX - newW / 2;
        state.y = drag.centerY - newH / 2;
      }
    }
    render();
  }

  function onPointerUp() {
    drag.active = false;
    drag.mode = null;
    canvas.classList.remove("grabbing");
    canvas.style.cursor = "";
  }

  /* Reset / Download */
  resetBtn.addEventListener("click", () => {
    roomImg = null;
    fileInput.value = "";
    scaleSlider.value   = 30;
    opacitySlider.value = 100;
    rotateSlider.value  = 0;
    if (bgRemoveSlider) bgRemoveSlider.value = bgThreshold;
    state = { x: 0, y: 0, scale: 0.30, opacity: 1, rotation: 0, flipped: false };
    editor.style.display     = "none";
    uploadArea.style.display = "";
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  });

  downloadBtn.addEventListener("click", () => {
    if (!roomImg) return;
    const offscreen = document.createElement("canvas");
    offscreen.width  = canvas.width;
    offscreen.height = canvas.height;
    const octx = offscreen.getContext("2d");
    octx.drawImage(roomImg, 0, 0, canvas.width, canvas.height);
    if (productLoaded && productImg) {
      const w = pw(), h = ph();
      const cx = state.x + w / 2, cy = state.y + h / 2;
      octx.save();
      octx.globalAlpha = state.opacity;
      octx.translate(cx, cy);
      octx.rotate(state.rotation);
      if (state.flipped) octx.scale(-1, 1);
      if (bgThreshold > 0) {
        octx.shadowColor = "rgba(0,0,0,0.35)";
        octx.shadowBlur = 28;
        octx.shadowOffsetY = 14;
      }
      octx.drawImage(productImg, -w / 2, -h / 2, w, h);
      octx.restore();
    }
    try {
      const link = document.createElement("a");
      link.download = "room-preview-" + productTitle.replace(/\s+/g, "-").toLowerCase() + ".png";
      link.href = offscreen.toDataURL("image/png");
      link.click();
    } catch (err) {
      alert("Could not save preview. The product image may be blocked by CORS.");
    }
  });

  window.addEventListener("resize", () => { if (roomImg) render(); });
})();
